import { Column, Container, Header, Row } from '@/components/SettingsLayout';
import { useGetUser } from '@/hooks';
import { Plan } from '@/services/Plans/plans.service';
import { Icon, IconButton, IconType, Text } from '@/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import accountsStyles from '../../styles.module.css';
import styles from './styles.module.css';
import { UserTypes } from '@/enums';
import { convertDateToFormat, formatDateToNow } from '@/utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetCompaniesCountByPlanIdQuery } from '@/store/slices/companySlice';
import classNames from 'classnames';
type Props = {
  baseUrl: string;
  plan?: Plan;
};
export const PlanLeftPart = ({ baseUrl, plan }: Props) => {
  const { t } = useTranslation();
  const { currentUser } = useGetUser();
  const navigate = useNavigate();
  const { data: companiesUsingPlan } = useGetCompaniesCountByPlanIdQuery(
    plan ? { organizationId: currentUser?.companyId || '', planId: plan._id } : skipToken,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const planId = searchParams.get('planId');
  return (
    <Container size={'small'}>
      <Column gapSize='24' fullWidth>
        <Column gapSize='12' fullWidth>
          <Row gapSize='8'>
            <Text size='xs' weight='light-medium' color='base-grey'>
              {t('Accounts.plans')}
            </Text>
            <Icon type={IconType.ArrowDown} width={14} height={14} fill='#D9D1E0' rotate='-90' />
          </Row>
          <Header>
            <div className={accountsStyles.headerInfoHolder}>
              {currentUser?.type !== UserTypes.RESELLER && (
                <IconButton onClick={() => navigate(`${baseUrl}`)} testId='plan-back-button'>
                  <Icon type={IconType.ArrowLeftLong} width={24} height={24} fill='#3D4C66' />
                </IconButton>
              )}
              <div className={accountsStyles.headerText}>
                <Text size='l' color='base-blue' weight='semi-bold'>
                  {plan ? plan?.name : t('Shared.new_plan')}
                </Text>
              </div>
            </div>
          </Header>
        </Column>
        {plan && (
          <div className={styles.companiesInPlan}>
            <Column fullWidth align='center' gapSize='none'>
              <Icon type={IconType.Bag} width={40} fill='transparent' />
              <Column gapSize='4' fullWidth align='center'>
                <Text size='m' weight='semi-bold'>
                  {companiesUsingPlan ?? 0}
                </Text>
                <Text size='xs' weight='light-medium'>
                  {t('Accounts.companies_in_this_plan')}
                </Text>
              </Column>
            </Column>
            <Column gapSize='16' fullWidth align='center'>
              <Row fullWidth justify='justify-between'>
                <Text size='xs' weight='light-medium'>
                  {t('Accounts.plan_created')}
                </Text>
                <Text size='xs' weight='light-medium'>
                  {convertDateToFormat(plan.createdAt)}
                </Text>
              </Row>
              <Row fullWidth justify='justify-between'>
                <Text size='xs' weight='light-medium'>
                  {t('Accounts.last_edit')}
                </Text>
                <Text size='xs' weight='light-medium'>
                  {formatDateToNow(plan.updatedAt)}
                </Text>
              </Row>
            </Column>
          </div>
        )}
        <Column gapSize='8' fullWidth>
          <button
            type='button'
            className={classNames(styles.link, {
              [styles.active]: searchParams.get('tab') === 'details',
            })}
            onClick={(e) => {
              e.preventDefault();
              setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                newParams.set('tab', 'details');
                return newParams;
              });
            }}
            data-testid='plan-left-part-details-button'
          >
            <Text size='s' weight='semi-bold'>
              {t('Shared.details')}
            </Text>
            <Icon type={IconType.DropDownIcon} width={24} height={24} rotate='-90' />
          </button>
          {planId && (
            <button
              className={classNames(styles.link, {
                [styles.active]: searchParams.get('tab') === 'pricing',
              })}
              onClick={() => {
                setSearchParams((prev) => {
                  const newParams = new URLSearchParams(prev);
                  newParams.set('tab', 'pricing');
                  return newParams;
                });
              }}
            >
              <Text size='s' weight='semi-bold'>
                {t('Pricing.pricing')}
              </Text>
              <Icon type={IconType.DropDownIcon} width={24} height={24} rotate='-90' />
            </button>
          )}
        </Column>
      </Column>
    </Container>
  );
};
